<template>
  <div class="renew-modal-options__container">
    <div class="renew-modal-options__header">
      <h2 class="renew-modal-options__title">
        Renew certification
      </h2>
      <div class="renew-modal-options__info">
        How would you like to renew your {{ certification.name }}
        certification?
      </div>
    </div>
    <RadioSelectorForm
      :options="currentOptions"
      v-bind="{ currentOption }"
      @select-option="toggleOptionSelection"
    />
    <div class="renew-modal-options__info-wrapper">
      <div>
        <div
          class="renew-modal-options__credits-label"
          v-html="currentOption.info"
        ></div>
        <div
          v-if="isOptionCerts && isAnyVoucherAvailable"
          class="renew-modal-options__info-text"
        >
          {{ readyToTakeExamLabel }} <br />
          <a
            class="renew-modal-options__link"
            v-if="anyPurchasableItem"
            @click="selectDifferentVoucher"
            >purchase a different voucher in this path.</a
          >
        </div>
        <div
          v-else
          class="renew-modal-options__info-text"
          v-html="currentOption.text"
        ></div>
      </div>
      <RadioSelectorForm
        v-if="isOptionCerts && isAnyVoucherAvailable"
        :options="userAvailableVouchers"
        :currentOption="currentCertSelection"
        class="renew-modal-options__certs-wrapper"
        @select-option="toggleCertSelection"
      />
      <div
        v-if="currentOption.footer"
        class="renew-modal-options__footer"
        v-html="currentOption.footer"
      ></div>
    </div>
    <div class="renew-modal-options__cta-wrapper">
      <button
        v-if="currentOption.btnLabel"
        class="btn btn--primary"
        :disabled="isBtnDisabled"
        @click="currentOption.action"
      >
        {{ currentOption.btnLabel }}
      </button>
      <div class="renew-modal-options__cta-info">
        <a
          :href="linkToCPEProgram"
          class="renew-modal-options__link"
          target="_blank"
        >
          Learn more
        </a>
        about certification renewals program and what qualifies as CPE credits.
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { voucherActionsCommons } from '@/helpers/mixins'
import { lastAttempt as certLastAttempt } from '@/helpers/certifications'
import RadioSelectorForm from '@/components/forms/RadioSelectorForm.vue'
import CONSTANTS from '@/constants'
import { vHelpers } from '@/helpers/vouchersCPE'
import { cloneDeep } from 'lodash'

const { STEPS, LINK_TO_CPE_PROGRAM } = CONSTANTS.CPE
const { isVoucherAvailable } = vHelpers

const OPTIONS = {
  CREDITS: 'credits',
  CERTS: 'certs',
  EXAM: 'exam',
}

const defaultOption = {
  show: false,
  name: null,
  info: null,
  text: null,
  footer: null,
  label: {},
  btnLabel: null,
}

export default {
  name: 'CPERenewModalOptions',
  components: {
    RadioSelectorForm,
  },
  mixins: [voucherActionsCommons],
  props: {
    certification: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    purchasableItems: {
      type: Array,
      required: false,
      default: () => [],
    },
    userActiveVouchers: {
      type: Array,
      required: true,
    },
    certPurchasableItem: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentCertSelection: null,
      options: [
        {
          ...cloneDeep(defaultOption),
          show: true,
          id: OPTIONS.CREDITS,
          name: `Submitting ${this.certification.cpe_credits_needed} CPE credits`,
        },
        {
          ...cloneDeep(defaultOption),
          id: OPTIONS.CERTS,
          btnLabel: 'Start exam',
        },
        {
          ...cloneDeep(defaultOption),
          show: true,
          id: OPTIONS.EXAM,
          name: `Passing the latest version of the ${this.certification.short_name} exam`,
        },
      ],
      elsExamSite: process.env.VUE_APP_ELS_EXAM_SITE,
      linkToCPEProgram: LINK_TO_CPE_PROGRAM,
    }
  },
  computed: {
    ...mapGetters({
      cpeGlobalCredits: 'cpeProgram/cpeGlobalCredits',
      cpeCreditHistoryExternal: 'cpeProgram/cpeCreditHistoryExternal',
      productCertificationVouchers:
        'subscriptions/getProductCertificationVouchers',
      currentOption: 'cpeProgram/cpeRenewModalOption',
    }),

    currentOptions() {
      return this.options.filter(option => option.show)
    },
    isBtnDisabled() {
      if (this.currentOption.id !== OPTIONS.CERTS) return false
      return this.isAnyVoucherAvailable && !this.currentCertSelection?.id
    },
    certVouchersNames() {
      return this.userAvailableVouchers.map(cert => cert.short_name).join(', ')
    },
    upperLevelVouchersNamesOption() {
      const length = this.items.length
      if (!length) return ''
      const names = this.items.map(cert => cert.name)
      return length > 1
        ? `${names.slice(0, -1).join(', ')} or ${names[length - 1]}`
        : names[0]
    },
    creditsAvailable() {
      const associatedExtCredits = this.cpeCreditHistoryExternal.reduce(
        (sum, credit) => {
          if (
            credit.restricted_to_content_id === this.certification?.attempt_id
          )
            return sum + (credit.credits || 0)
          return sum
        },
        0
      )
      return this.cpeGlobalCredits + associatedExtCredits
    },
    canRenew() {
      return this.creditsAvailable >= this.certification.cpe_credits_needed
    },
    creditsNeeded() {
      return this.certification.cpe_credits_needed - this.creditsAvailable
    },
    creditsNeededLabel() {
      return `${this.creditsNeeded} more credit${
        this.creditsNeeded > 1 ? 's' : ''
      }`
    },
    isAnyVoucherAvailable() {
      return this.userAvailableVouchers.length > 0
    },
    isAnyUpperLevelVoucher() {
      return this.items.length > 0
    },
    hasAnyPlan() {
      return this.items.some(item => item?.type === 'plan')
    },
    isCurrentCertPlanType() {
      return this.certPurchasableItem?.type === 'plan'
    },
    certificationVoucherVersionsIds() {
      return this.certification.versions.map(v => v.id)
    },
    currentCertPurchasableVoucher() {
      return this.productCertificationVouchers.find(v =>
        this.certificationVoucherVersionsIds.includes(v.id)
      )
    },
    currentPurchasableVoucher() {
      return this.productCertificationVouchers.find(
        v => this.currentCertSelection?.id === v.id
      )
    },
    upperLevelItemsIds() {
      return this.certification.upper_level_certifications.map(v => v.id)
    },
    userAvailableVouchers() {
      return this.userActiveVouchers
        .filter(voucher => {
          const upperLevelVoucher = this.upperLevelItemsIds.includes(voucher.id)
          return upperLevelVoucher && isVoucherAvailable(voucher)
        })
        .map(voucher => ({
          ...voucher,
          name: voucher.short_name,
        }))
    },
    lastAttempt() {
      return certLastAttempt(this.certification)
    },
    voucher() {
      return this.hasVouchersAvailable(
        this.currentCertSelection?.versions[0]?.id
      )
    },
    isLatestVoucherAvailable() {
      const voucher = this.hasVouchersAvailable(
        this.certification?.versions[0]?.id
      )
      return isVoucherAvailable(voucher)
    },
    isOptionCerts() {
      return this.currentOption?.id === OPTIONS.CERTS
    },
    currentCertLabel() {
      const clarification = this.isCurrentCertPlanType ? '*' : ''
      return `${this.certification.short_name}${clarification}`
    },
    anyPurchasableItem() {
      return this.purchasableItems.length > 0
    },
    readyToTakeExamLabel() {
      const additionalText = this.anyPurchasableItem ? 'or' : ''
      return `Ready to take an exam now? Select one below to start ${additionalText}`
    },
    isExternal() {
      return this.voucher?.is_external
    },
    isItemPlural() {
      return this.items.length > 1
    },
    itemsPlural() {
      return this.isItemPlural ? 's' : ''
    },
    vouchersAvailableLabel() {
      const purchasingLabel = this.isItemPlural
        ? 'one of the more advanced'
        : 'the more advanced'
      return `Otherwise, begin by purchasing ${purchasingLabel} ${this.upperLevelVouchersNamesOption} exam voucher${this.itemsPlural}.`
    },
  },
  created() {
    this.setRenewModalStatus({
      currentOption: this.currentOption || this.options[0],
    })
    this.setupOptions()
  },
  methods: {
    ...mapActions({
      setRenewModalStatus: 'cpeProgram/setRenewModalStatus',
      setRenewModalStep: 'cpeProgram/setRenewModalStep',
    }),
    toggleOptionSelection(option) {
      const certValue = option.id === OPTIONS.EXAM ? this.certification : null
      this.toggleCertSelection(certValue)
      this.setRenewModalStatus({
        currentOption: option,
        isCurrentCert: option.id === OPTIONS.EXAM,
      })
    },
    toggleCertSelection(cert) {
      this.setRenewModalStatus({
        currentSelection: cert,
      })
      this.currentCertSelection = cert
    },
    selectDifferentVoucher() {
      this.setModalStatus({
        step: STEPS.PURCHASE,
      })
    },
    setModalStatus(values) {
      const params = {
        isCurrentCert: false,
      }
      this.setRenewModalStatus({ ...params, ...values })
    },
    hasVouchersAvailable(item) {
      return this.userActiveVouchers.find(voucher => {
        const voucherIds = voucher.versions.map(v => v.id)
        return voucherIds.includes(item)
      })
    },
    goToExamHandler() {
      if (!this.isExternal) return this.goToExam()
      const externalLink = `//${this.elsExamSite}/exams`
      window.open(externalLink, '_blank', 'noopener,noreferrer')
      this.$parent.$emit('close-modal')
    },
    setupOptions() {
      if (this.canRenew) {
        this.options[0].info = `You have <span class="renew-modal-options__credits-label--credits">${this.creditsAvailable} CPE</span> credits available.`
        this.options[0].text = `Would you like to submit ${this.certification.cpe_credits_needed} credits to renew?`
        this.options[0].btnLabel = 'Yes, continue'
        this.options[0].action = () =>
          this.setModalStatus({
            step: STEPS.PAYMENT,
            isRenewal: true,
            voucher: null,
            certification: this.certification,
          })
      } else {
        this.options[0].info = `You need <span class="renew-modal-options__credits-label--no-credits">${this.creditsNeededLabel}</span> to renew.`
        this.options[0].text =
          'Earn more credits by completing INE training courses, Skill Dive CTF labs and Skill Check labs.'
        this.options[0].action = () => null
      }
      this.options[1].show = this.isAnyUpperLevelVoucher
      this.options[1].name = `Earning ${this.upperLevelVouchersNamesOption} certification${this.itemsPlural}`
      this.options[1].label.footnote = this.hasAnyPlan
        ? '*Subscription required'
        : ''
      if (this.isAnyVoucherAvailable) {
        this.options[1].info = `You have the following voucher(s): ${this.certVouchersNames}.`
        this.options[1].footer = `If you've already earned a more advanced certification in this path, <a href="mailto:support@ine.com">contact us to renew.</a>`
        this.options[1].btnLabel = 'Start exam'
        this.options[1].action = this.goToExamHandler
      } else {
        this.options[1].info = `<div class="renew-modal-options__contact-info">If you've already earned a more advanced certification in this path, <a href="mailto:support@ine.com">contact us to renew.</a></div>${this.vouchersAvailableLabel}`
        this.options[1].text = `Are you ready to continue?`
        this.options[1].btnLabel = 'Yes, continue'
        this.options[1].action = () =>
          this.setModalStatus({
            step: STEPS.PURCHASE,
            currentSelection: this.currentPurchasableVoucher,
          })
      }
      if (this.isLatestVoucherAvailable) {
        this.options[2].info = `You have the latest version of the ${this.certification.short_name} exam voucher.`
        this.options[2].text = `Are you ready to continue?`
        this.options[2].btnLabel = 'Start exam'
        this.options[2].action = this.goToExamHandler
      } else {
        this.options[2].name = `Passing the latest version of the ${this.currentCertLabel} exam`
        this.options[2].info = `Begin by purchasing the latest ${this.currentCertLabel} exam voucher.`
        this.options[2].text = `Are you ready to continue?`
        this.options[2].label.footnote = this.isCurrentCertPlanType
          ? '*Subscription required'
          : ''
        this.options[2].btnLabel = 'Yes, continue'
        this.options[2].action = () =>
          this.setModalStatus({
            step: STEPS.PURCHASE,
            isCurrentCert: true,
            currentSelection: this.certPurchasableItem,
          })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.renew-modal-options {
  &__container {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }
  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  &__title {
    font-size: rem(32);
    line-height: rem(40);
    margin: 0;
    text-align: center;
    @include mobile {
      font-size: rem(40);
      line-height: rem(52);
      margin: 0;
    }
  }
  &__info {
    margin-top: 20px;
    color: var(--light-blue-gray);
    font-size: rem(16);
    line-height: rem(24);
    text-align: center;
  }
  div {
    align-items: center;
  }
  &__options-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    @include mobile {
      flex-wrap: wrap;
      gap: 5px;
    }
    @include tablet {
      gap: 10px;
    }
  }
  &__certs-wrapper {
    margin-top: 30px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  &__item {
    width: 100%;
    font-size: rem(16);
    display: flex;
    align-items: center;
    line-height: 24px;
    color: var(--white);
    padding: 10px 10px 10px 20px;
    margin-bottom: 5px;
    border-radius: 10px;
    height: 44px;
    transition: 0.2s height;

    cursor: pointer;
    @include mobile {
      padding: 30px;
      margin-bottom: 0;
    }
    @include tablet {
      padding: 10px 10px 10px 20px;
      height: 54px;
    }
    &-description {
      display: flex;
      align-items: center;
    }
    &-icon {
      width: 30px;
      @include tablet {
        width: 50px;
      }
    }
    &-label {
      margin-left: 10px;
    }
  }

  &__input {
    &:checked {
      background: var(--purple);
      border-color: var(--purple);
    }
  }
  &__control {
    width: 30px;
    height: 30px;
    border-width: 1px;
  }
  &__info-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: rem(16);
    line-height: rem(24);
    color: var(--white);
    font-weight: 700;
    text-align: center;
  }
  &__info-text {
    color: var(--light-blue-gray);
    ::v-deep a {
      text-decoration: none;
      color: var(--purple-pale);
      cursor: pointer;
    }
  }
  ::v-deep &__credits-label--credits {
    color: var(--ice-cold);
  }
  ::v-deep &__credits-label--no-credits {
    color: var(--red-lighter);
  }

  @mixin link {
    text-decoration: none;
    color: var(--purple-pale);
    cursor: pointer;
    font-weight: 450;
  }

  &__credits-label {
    ::v-deep .renew-modal-options__contact-info {
      font-weight: 450;
      color: var(--light-blue-gray);
      a {
        @include link;
      }
    }
  }
  &__cta-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    margin-bottom: 50px;
  }
  &__cta-info {
    color: var(--light-blue-gray);
  }
  &__footer {
    margin-top: 10px;
    color: var(--light-blue-gray);
    font-size: rem(16);
    line-height: rem(24);
    text-align: center;
    ::v-deep a {
      @include link;
    }
  }
  &__link {
    @include link;
  }
}
</style>
